import { memo, useCallback, useEffect, useRef, type RefObject } from "react";
import {
  hitTestDeep,
  hitTestLatexOffset,
  injectCursorStyles,
  renderAstWithCursor,
  type LatexCursor,
  type LatexDocument,
} from "@ever-tutor/et-ui";

import { renderStaticDocument } from "@/features/tutor/utils/renderLatexField";
import { renderMath } from "@/utils/mathjax";

export interface MathTextFieldProps {
  fieldRef: RefObject<HTMLDivElement>;
  doc: LatexDocument;
  cursor: LatexCursor;
  selectionAnchor: LatexCursor | null;
  active: boolean;
  placeholder?: string;
  onSelectionStart: (cursor: LatexCursor) => void;
  onSelectionExtend: (offset: number) => void;
  onSelectionEnd: () => void;
  onFocusChange: (focused: boolean) => void;
  ariaLabel: string;
  describedBy?: string;
}

const MathTextField = ({
  fieldRef,
  doc,
  cursor,
  selectionAnchor,
  active,
  placeholder,
  onSelectionStart,
  onSelectionExtend,
  onSelectionEnd,
  onFocusChange,
  ariaLabel,
  describedBy,
}: MathTextFieldProps) => {
  useEffect(() => {
    injectCursorStyles();
  }, []);

  const isEmpty = doc.nodes.length === 0;

  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;

    if (isEmpty && !active) {
      el.classList.remove("et-katex-editing");
      el.textContent = placeholder ?? "";
      el.dataset.placeholder = "true";
      return;
    }

    delete el.dataset.placeholder;

    if (!active) {
      el.classList.remove("et-katex-editing");
      el.querySelector(".latex-cursor-dom")?.remove();
      renderStaticDocument(el, doc);
      return;
    }

    el.classList.add("et-katex-editing");
    renderAstWithCursor(el, doc, cursor, selectionAnchor, renderMath);
  }, [fieldRef, doc, cursor, selectionAnchor, isEmpty, active, placeholder]);

  const pointerPlaced = useRef(false);

  const endOfDocument = useCallback(
    (): LatexCursor => ({
      containerId: "root",
      containerField: "nodes",
      offset: doc.nodes.length,
    }),
    [doc],
  );

  const placeFromPointer = useCallback(
    (clientX: number, clientY: number): LatexCursor => {
      const el = fieldRef.current;
      if (!el || doc.nodes.length === 0) {
        return { containerId: "root", containerField: "nodes", offset: 0 };
      }

      const deep = hitTestDeep(el, doc, clientX, clientY);
      if (deep.containerId !== "root") return deep;

      // A click in the empty space right of the text hits no node, and the deep
      // test answers offset 0 — which parked the caret at the start. The
      // root-level x-to-offset test clamps to the nearest position instead.
      return {
        containerId: "root",
        containerField: "nodes",
        offset: hitTestLatexOffset(el, clientX, clientY),
      };
    },
    [fieldRef, doc],
  );

  const onMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const el = fieldRef.current;
      if (!el || event.button !== 0) return;

      event.preventDefault();
      pointerPlaced.current = true;
      el.focus();
      onSelectionStart(placeFromPointer(event.clientX, event.clientY));

      const onMouseMove = (move: MouseEvent) => {
        onSelectionExtend(hitTestLatexOffset(el, move.clientX, move.clientY));
      };
      const onMouseUp = () => {
        onSelectionEnd();
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [
      fieldRef,
      placeFromPointer,
      onSelectionStart,
      onSelectionExtend,
      onSelectionEnd,
    ],
  );

  const onFocus = useCallback(() => {
    onFocusChange(true);
    // Tab or a programmatic focus has no pointer to aim with, so resume at the
    // end of what is written rather than jumping to the start.
    if (!pointerPlaced.current) onSelectionStart(endOfDocument());
    pointerPlaced.current = false;
  }, [onFocusChange, onSelectionStart, endOfDocument]);

  return (
    <div
      ref={fieldRef}
      role="textbox"
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      aria-multiline="true"
      tabIndex={0}
      onMouseDown={onMouseDown}
      onFocus={onFocus}
      onBlur={() => onFocusChange(false)}
      className="et-katex-display chat-math-field max-h-[140px] min-h-[32px] w-full cursor-text select-none overflow-y-auto whitespace-pre-wrap px-2 py-1.5 text-[#111] outline-none data-[placeholder]:text-[#9AA6AD]"
    />
  );
};

export default memo(MathTextField);
